import React, { useState } from 'react';
import { X, Clock, Target, Users, Sparkles } from 'lucide-react';

const MatchingPreferencesModal = ({ onClose, onComplete }) => {
  const [preferences, setPreferences] = useState({
    preferred_availability: '',
    goal_category: '',
    accountability_style: ''
  });
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const availabilityOptions = [
    { value: 'morning', label: '🌅 Morning', desc: '6am - 12pm' },
    { value: 'afternoon', label: '☀️ Afternoon', desc: '12pm - 6pm' },
    { value: 'evening', label: '🌙 Evening', desc: '6pm - 12am' },
    { value: 'flexible', label: '⚡ Flexible', desc: 'Anytime' }
  ];

  const goalCategories = [
    { value: 'business', label: '💼 Business', desc: 'Entrepreneurship, career growth' },
    { value: 'fitness', label: '💪 Fitness', desc: 'Health, exercise, wellness' },
    { value: 'school', label: '📚 School', desc: 'Academic, learning' },
    { value: 'self_improvement', label: '🌟 Self-Improvement', desc: 'Personal development' },
    { value: 'mixed_productivity', label: '⚡ Mixed Productivity', desc: 'Various goals' }
  ];

  const accountabilityStyles = [
    { value: 'strict', label: '🎯 Strict', desc: 'Direct, no-nonsense feedback' },
    { value: 'supportive', label: '🤝 Supportive', desc: 'Encouraging and empathetic' },
    { value: 'neutral', label: '⚖️ Neutral', desc: 'Balanced approach' }
  ];

  const handleSaveAndMatch = async () => {
    if (!preferences.preferred_availability || !preferences.goal_category || !preferences.accountability_style) {
      alert('Please select all preferences');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Save preferences
      const prefsResponse = await fetch(`${BACKEND_URL}/api/member/accountability/v2/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (!prefsResponse.ok) {
        throw new Error('Failed to save preferences');
      }

      // Auto-match
      const matchResponse = await fetch(`${BACKEND_URL}/api/member/accountability/v2/auto-match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (matchResponse.ok) {
        const data = await matchResponse.json();
        alert(`🎉 Matched! You've been paired with ${data.partner.name}!`);
        onComplete();
        onClose();
      } else {
        const error = await matchResponse.json();
        if (matchResponse.status === 404) {
          alert('No compatible partners found at the moment. Your preferences have been saved. We\'ll notify you when a match is available!');
          onComplete();
          onClose();
        } else {
          throw new Error(error.detail || 'Failed to find match');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Failed to complete matching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Find Your Perfect Partner</h2>
              <p className="text-amber-100 mt-1">Set your preferences for better matching</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Availability */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold">When are you most available?</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {availabilityOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setPreferences({ ...preferences, preferred_availability: option.value })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    preferences.preferred_availability === option.value
                      ? 'border-amber-600 bg-amber-50'
                      : 'border-gray-300 hover:border-amber-400'
                  }`}
                >
                  <p className="font-semibold">{option.label}</p>
                  <p className="text-sm text-gray-600">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Goal Category */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold">What's your main focus?</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {goalCategories.map(option => (
                <button
                  key={option.value}
                  onClick={() => setPreferences({ ...preferences, goal_category: option.value })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    preferences.goal_category === option.value
                      ? 'border-amber-600 bg-amber-50'
                      : 'border-gray-300 hover:border-amber-400'
                  }`}
                >
                  <p className="font-semibold">{option.label}</p>
                  <p className="text-sm text-gray-600">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Accountability Style */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold">Preferred accountability style?</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {accountabilityStyles.map(option => (
                <button
                  key={option.value}
                  onClick={() => setPreferences({ ...preferences, accountability_style: option.value })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    preferences.accountability_style === option.value
                      ? 'border-amber-600 bg-amber-50'
                      : 'border-gray-300 hover:border-amber-400'
                  }`}
                >
                  <p className="font-semibold">{option.label}</p>
                  <p className="text-sm text-gray-600">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-blue-600 mt-1" />
              <div className="flex-1">
                <p className="font-semibold text-blue-900 mb-1">Smart Matching</p>
                <p className="text-sm text-blue-700">
                  We'll find you a partner who matches your preferences and goals. 
                  Partners rotate every 2 weeks for fresh accountability and broader connections.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={handleSaveAndMatch}
            disabled={loading || !preferences.preferred_availability || !preferences.goal_category || !preferences.accountability_style}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Finding Your Perfect Match...
              </span>
            ) : (
              'Find My Partner'
            )}
          </button>
          <p className="text-xs text-gray-600 text-center mt-2">
            You can update your preferences anytime in settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default MatchingPreferencesModal;
